/* Bank Adjustment Center — Phase 2 UI (Feb 2026).
 *
 * Wizard-driven page that lets an operator post and void Bank
 * Adjustments (Bank Charges / Interest Income / FX Gain / FX Loss /
 * Opening Balance Correction / Other) without ever having to pick
 * the counter GL account themselves — the backend Policy Engine
 * resolves it from the adjustment type.
 *
 * Layout
 *   1) KPI row             — Posted count / Voided count / Net movement
 *   2) "New Adjustment"    — 4-step wizard (Type → Bank → Amount/Date → Review)
 *   3) Filters + table     — list, statuses, jump to journal
 *   4) Detail drawer       — full row + Void modal (admin)
 *
 * NOTE: every interactive element has a data-testid for the testing
 * agent. Strings are bilingual (AR/EN) inline to match the rest of
 * the app's UX convention.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';

import {
  Banknote, Plus, X, Check, ChevronRight, ChevronLeft, RefreshCw,
  TrendingUp, TrendingDown, AlertCircle, FileText, BookOpen,
  CalendarDays, Wallet, RotateCcw, Filter, Loader2, UserMinus, UserPlus,
} from 'lucide-react';

const STATUS_COLOR = {
  posted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  voided: 'bg-rose-50 text-rose-700 border-rose-200',
};

const STATUS_LABEL = {
  posted: 'Posted · مُسجَّل',
  voided: 'Voided · ملغى',
};

const TYPE_ICONS = {
  bank_charge:                <TrendingDown className="w-4 h-4" />,
  interest_income:            <TrendingUp className="w-4 h-4" />,
  fx_gain:                    <TrendingUp className="w-4 h-4" />,
  fx_loss:                    <TrendingDown className="w-4 h-4" />,
  opening_balance_correction: <RotateCcw className="w-4 h-4" />,
  owner_withdrawal:           <UserMinus className="w-4 h-4" />,
  owner_deposit:              <UserPlus className="w-4 h-4" />,
  other:                      <FileText className="w-4 h-4" />,
};

const OWNER_TYPES = ['owner_withdrawal', 'owner_deposit'];

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export default function BankAdjustments({ user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // ── data ─────────────────────────────────────────────────────────
  const [types, setTypes]               = useState([]);
  const [banks, setBanks]               = useState([]);
  const [partners, setPartners]         = useState([]);
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(true);

  // ── filters ───────────────────────────────────────────────────────
  const [filterBank, setFilterBank]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterStart, setFilterStart]     = useState('');
  const [filterEnd, setFilterEnd]         = useState('');

  // ── wizard state ──────────────────────────────────────────────────
  const [wizardOpen, setWizardOpen]     = useState(false);
  const [step, setStep]                 = useState(1);
  const [form, setForm]                 = useState({
    adjustment_type:      '',
    bank_account_id:      '',
    amount:               '',
    adjustment_date:      new Date().toISOString().slice(0, 10),
    description:          '',
    reference:            '',
    counter_account_code: '',
    counter_account_id:   '',
    // VAT (ZATCA — Feb 2026) — surfaced only for bank_charge.
    vat_amount:           '',
    has_tax_invoice:      false,
  });
  const [submitting, setSubmitting]     = useState(false);

  // ── detail drawer + void ─────────────────────────────────────────
  const [detail, setDetail]             = useState(null);
  const [voidReason, setVoidReason]     = useState('');
  const [voiding, setVoiding]           = useState(false);

  // ── load ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterBank)   q.set('bank_account_id', filterBank);
      if (filterStatus) q.set('status', filterStatus);
      if (filterStart)  q.set('start_date', filterStart);
      if (filterEnd)    q.set('end_date',   filterEnd);
      const [tRes, bRes, lRes, pRes] = await Promise.all([
        axiosInstance.get('/bank/adjustments/types'),
        axiosInstance.get('/bank/accounts'),
        axiosInstance.get(`/bank/adjustments?${q.toString()}`),
        axiosInstance.get('/bank/adjustments/partner-accounts'),
      ]);
      setTypes(tRes.data?.types || []);
      setBanks(bRes.data || []);
      setRows(lRes.data || []);
      setPartners(pRes.data || []);
    } catch (e) {
      toast.error('فشل تحميل البيانات / Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterBank, filterStatus, filterStart, filterEnd]);

  useEffect(() => { load(); }, [load]);

  // ── KPIs ─────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const posted = rows.filter(r => r.status === 'posted');
    const voided = rows.filter(r => r.status === 'voided');
    const netUp = posted.reduce(
      (acc, r) => acc + (r.direction === 'debit'  ? Number(r.amount) : 0), 0,
    );
    const netDown = posted.reduce(
      (acc, r) => acc + (r.direction === 'credit' ? Number(r.amount) : 0), 0,
    );
    return {
      postedCount: posted.length,
      voidedCount: voided.length,
      net:         netUp - netDown,
    };
  }, [rows]);

  // ── wizard helpers ───────────────────────────────────────────────
  const resetWizard = () => {
    setStep(1);
    setForm({
      adjustment_type:      '',
      bank_account_id:      '',
      amount:               '',
      adjustment_date:      new Date().toISOString().slice(0, 10),
      description:          '',
      reference:            '',
      counter_account_code: '',
      counter_account_id:   '',
      vat_amount:           '',
      has_tax_invoice:      false,
    });
  };
  const openWizard = () => { resetWizard(); setWizardOpen(true); };

  const selectedType = types.find(t => t.value === form.adjustment_type);
  const selectedBank = banks.find(b => b.id === form.bank_account_id);

  const stepValid = (s) => {
    if (s === 1) return !!form.adjustment_type;
    if (s === 2) return !!form.bank_account_id;
    if (s === 3) {
      const amt = parseFloat(form.amount);
      const hasOther = form.adjustment_type !== 'other' ||
                        !!form.counter_account_code;
      const hasPartner = !OWNER_TYPES.includes(form.adjustment_type) ||
                          !!form.counter_account_id;
      // VAT guardrails when applicable (bank_charge with a tax invoice).
      const vat = parseFloat(form.vat_amount) || 0;
      if (vat > 0) {
        if (form.adjustment_type !== 'bank_charge') return false;
        if (!form.has_tax_invoice) return false;
        if (!(vat < amt)) return false;
      }
      return amt > 0 && !!form.adjustment_date && !!form.description.trim() && hasOther && hasPartner;
    }
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        bank_account_id: form.bank_account_id,
        adjustment_type: form.adjustment_type,
        amount:          parseFloat(form.amount),
        adjustment_date: form.adjustment_date,
        description:     form.description.trim(),
        reference:       form.reference || null,
        counter_account_code: form.counter_account_code || null,
        counter_account_id:   form.counter_account_id || null,
        vat_amount:      parseFloat(form.vat_amount) || 0,
        has_tax_invoice: !!form.has_tax_invoice,
      };
      const r = await axiosInstance.post('/bank/adjustments', payload);
      toast.success(
        `${r.data.adjustment_no} — تم الترحيل بنجاح / Posted successfully`,
      );
      setWizardOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الترحيل / Post failed');
    } finally {
      setSubmitting(false);
    }
  };

  const addPartner = async (name) => {
    try {
      const r = await axiosInstance.post('/bank/adjustments/partner-accounts', { name });
      setPartners(prev => [...prev, r.data]);
      setForm(f => ({ ...f, counter_account_id: r.data.id }));
      toast.success(`تم إنشاء «${r.data.account_name}» (${r.data.account_number})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل إنشاء حساب الشريك');
    }
  };

  const doVoid = async () => {
    if (!detail) return;
    setVoiding(true);
    try {
      await axiosInstance.post(
        `/bank/adjustments/${detail.id}/void`,
        { reason: voidReason },
      );
      toast.success('تم الإلغاء وإنشاء قيد عكسي / Voided + reversal posted');
      setDetail(null);
      setVoidReason('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الإلغاء / Void failed');
    } finally {
      setVoiding(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6" data-testid="bank-adjustments-page">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Banknote className="w-7 h-7 text-amber-600" />
            تَعديلات البنك / Bank Adjustments
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            القَناة الوحيدة المسموح بها لإنشاء قَيد محاسبي من حدث بنكي
            (عمولات، فوائد، فروق عملة، تَصحيح رصيد افتتاحي). كل تَعديل ⇒
            مُستند واحد ⇒ قيد واحد ⇒ غير قابل للحذف بل للإلغاء فقط.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="badj-refresh-btn"
            onClick={load}
            className="px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="badj-new-btn"
            onClick={openWizard}
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-2 text-sm font-medium shadow-sm"
          >
            <Plus className="w-4 h-4" /> تَعديل جديد / New Adjustment
          </button>
        </div>
      </div>

      {/* ── KPIs ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Posted · مُسجَّل" value={kpis.postedCount}
              tone="emerald" testid="kpi-posted" />
        <KPI label="Voided · ملغى" value={kpis.voidedCount}
              tone="rose" testid="kpi-voided" />
        <KPI label="Net Movement · صافي الأثر" value={fmtMoney(kpis.net) + ' SAR'}
              tone={kpis.net >= 0 ? 'emerald' : 'rose'} testid="kpi-net" />
      </div>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="flex items-center gap-2 col-span-1">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-500">Filters</span>
        </div>
        <select
          data-testid="filter-bank"
          value={filterBank}
          onChange={(e) => setFilterBank(e.target.value)}
          className="form-select text-sm"
        >
          <option value="">All banks</option>
          {banks.map(b => (
            <option key={b.id} value={b.id}>
              {b.bank_name} — {b.account_number?.slice(-4)}
            </option>
          ))}
        </select>
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="form-select text-sm"
        >
          <option value="">All statuses</option>
          <option value="posted">Posted</option>
          <option value="voided">Voided</option>
        </select>
        <input
          data-testid="filter-start-date"
          type="date" value={filterStart}
          onChange={(e) => setFilterStart(e.target.value)}
          className="form-input text-sm"
        />
        <input
          data-testid="filter-end-date"
          type="date" value={filterEnd}
          onChange={(e) => setFilterEnd(e.target.value)}
          className="form-input text-sm"
        />
      </div>

      {/* ── Table ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" data-testid="adjustments-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-start px-3 py-2">No.</th>
              <th className="text-start px-3 py-2">Date</th>
              <th className="text-start px-3 py-2">Type</th>
              <th className="text-start px-3 py-2">Bank</th>
              <th className="text-end px-3 py-2">Amount</th>
              <th className="text-start px-3 py-2">Direction</th>
              <th className="text-start px-3 py-2">Status</th>
              <th className="text-start px-3 py-2">Description</th>
              <th className="text-end px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-8 text-slate-400">
                <Loader2 className="inline w-4 h-4 animate-spin mr-2" />
                Loading...
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-slate-400">
                لا توجد تَعديلات بعد — اضغط &quot;تَعديل جديد&quot; / No adjustments yet.
              </td></tr>
            )}
            {!loading && rows.map(r => (
              <tr
                key={r.id}
                data-testid={`adj-row-${r.id}`}
                className="border-t border-slate-100 hover:bg-slate-50"
              >
                <td className="px-3 py-2 font-mono text-xs">{r.adjustment_no}</td>
                <td className="px-3 py-2">{r.adjustment_date}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {TYPE_ICONS[r.adjustment_type]}
                    <span className="text-xs">
                      {types.find(t => t.value === r.adjustment_type)?.label_en
                        || r.adjustment_type}
                    </span>
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{r.bank_account_name}</td>
                <td className="px-3 py-2 text-end font-semibold">
                  {fmtMoney(r.amount)}
                </td>
                <td className="px-3 py-2">
                  {r.direction === 'debit' ? (
                    <span className="text-emerald-700 inline-flex items-center gap-1 text-xs">
                      <TrendingUp className="w-3 h-3" /> Bank ↑
                    </span>
                  ) : (
                    <span className="text-rose-700 inline-flex items-center gap-1 text-xs">
                      <TrendingDown className="w-3 h-3" /> Bank ↓
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[r.status]}`}
                    data-testid={`adj-status-${r.id}`}
                  >
                    {STATUS_LABEL[r.status]}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-xs truncate">{r.description}</td>
                <td className="px-3 py-2 text-end space-x-2">
                  <button
                    data-testid={`adj-view-${r.id}`}
                    onClick={() => setDetail(r)}
                    className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100"
                  >
                    View
                  </button>
                  {r.journal_id && (
                    <button
                      data-testid={`adj-journal-${r.id}`}
                      onClick={() => navigate(`/journal?focus=${r.journal_id}`)}
                      className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                    >
                      <BookOpen className="w-3 h-3" /> Journal
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Wizard modal ─────────────────────────────────────────── */}
      {wizardOpen && (
        <Wizard
          step={step}
          setStep={setStep}
          form={form}
          setForm={setForm}
          types={types}
          banks={banks}
          partners={partners}
          addPartner={addPartner}
          selectedType={selectedType}
          selectedBank={selectedBank}
          stepValid={stepValid}
          submitting={submitting}
          submit={submit}
          onClose={() => setWizardOpen(false)}
        />
      )}

      {/* ── Detail drawer + Void ─────────────────────────────────── */}
      {detail && (
        <DetailDrawer
          row={detail}
          isAdmin={isAdmin}
          voidReason={voidReason}
          setVoidReason={setVoidReason}
          voiding={voiding}
          onVoid={doVoid}
          onClose={() => { setDetail(null); setVoidReason(''); }}
          onJumpJournal={(jid) => navigate(`/journal?focus=${jid}`)}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// KPI card
// ─────────────────────────────────────────────────────────────────────
function KPI({ label, value, tone = 'slate', testid }) {
  const toneCls = {
    emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-900',
    rose:    'border-rose-200 bg-rose-50/50 text-rose-900',
    slate:   'border-slate-200 bg-white text-slate-900',
  }[tone] || '';
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-4 ${toneCls}`}
    >
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Wizard component — 4 steps
// ─────────────────────────────────────────────────────────────────────
function Wizard({
  step, setStep, form, setForm, types, banks, partners, addPartner,
  selectedType, selectedBank, stepValid, submitting, submit, onClose,
}) {
  const steps = ['Type', 'Bank', 'Details', 'Review'];
  return (
    <div
      data-testid="adj-wizard"
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl mt-10">
        {/* header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-600" />
            تَعديل بنكي جديد / New Bank Adjustment
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* stepper */}
        <div className="px-6 py-4 flex items-center justify-between gap-2">
          {steps.map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done   = step > n;
            return (
              <div key={label} className="flex items-center flex-1">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                    done ? 'bg-emerald-500 border-emerald-500 text-white' :
                    active ? 'border-amber-500 text-amber-700' :
                    'border-slate-200 text-slate-400'
                  }`}
                >
                  {done ? <Check className="w-3 h-3" /> : n}
                </div>
                <div className={`mx-2 text-xs ${active ? 'font-bold' : 'text-slate-500'}`}>
                  {label}
                </div>
                {n < 4 && <div className="flex-1 h-px bg-slate-200" />}
              </div>
            );
          })}
        </div>

        {/* body */}
        <div className="px-6 py-4 min-h-[260px]">
          {step === 1 && (
            <StepType types={types} value={form.adjustment_type}
                       onChange={(v) => setForm({ ...form, adjustment_type: v })} />
          )}
          {step === 2 && (
            <StepBank banks={banks} value={form.bank_account_id}
                       onChange={(v) => setForm({ ...form, bank_account_id: v })} />
          )}
          {step === 3 && (
            <StepDetails form={form} setForm={setForm}
                          selectedType={selectedType}
                          partners={partners} addPartner={addPartner} />
          )}
          {step === 4 && (
            <StepReview form={form}
                         selectedType={selectedType}
                         selectedBank={selectedBank}
                         partners={partners} />
          )}
        </div>

        {/* footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <button
            data-testid="wizard-back-btn"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1 || submitting}
            className="px-3 py-2 text-sm rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < 4 ? (
            <button
              data-testid="wizard-next-btn"
              onClick={() => setStep(step + 1)}
              disabled={!stepValid(step)}
              className="px-4 py-2 text-sm rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 inline-flex items-center gap-1"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="wizard-submit-btn"
              onClick={submit}
              disabled={submitting}
              className="px-4 py-2 text-sm rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 inline-flex items-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" />
                             : <Check className="w-4 h-4" />}
              تأكيد وترحيل / Confirm & Post
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Wizard steps
// ─────────────────────────────────────────────────────────────────────
function StepType({ types, value, onChange }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        اختَر نوع التَعديل — الحساب المُقابِل يُحَدَّد تلقائياً من الـ Policy Engine.
      </p>
      <div className="grid grid-cols-2 gap-3" data-testid="wizard-type-grid">
        {types.map(t => {
          const active = value === t.value;
          return (
            <button
              key={t.value}
              data-testid={`wizard-type-${t.value}`}
              onClick={() => onChange(t.value)}
              className={`text-left p-3 rounded-lg border-2 transition-all ${
                active ? 'border-amber-500 bg-amber-50' :
                          'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2">
                {TYPE_ICONS[t.value]}
                <div className="font-medium text-sm">{t.label_en}</div>
              </div>
              <div className="text-xs text-slate-500 mt-1">{t.label_ar}</div>
              {t.default_counter && (
                <div className="text-[10px] text-slate-400 mt-2 font-mono">
                  → {t.default_counter}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepBank({ banks, value, onChange }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        اختَر الحساب البنكي المُتأثِّر بهذا التَعديل.
      </p>
      {banks.length === 0 ? (
        <div className="p-4 rounded border border-amber-200 bg-amber-50 text-sm text-amber-800">
          لا توجد حسابات بنكية بعد — أضِف حساباً من صفحة Bank أولاً.
        </div>
      ) : (
        <div className="space-y-2" data-testid="wizard-bank-list">
          {banks.map(b => {
            const active = value === b.id;
            return (
              <button
                key={b.id}
                data-testid={`wizard-bank-${b.id}`}
                onClick={() => onChange(b.id)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                  active ? 'border-amber-500 bg-amber-50' :
                            'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div>
                  <div className="font-medium text-sm">{b.bank_name}</div>
                  <div className="text-xs text-slate-500">
                    {b.account_name} · ****{(b.account_number || '').slice(-4)}
                  </div>
                </div>
                <div className="text-end">
                  <div className="text-xs text-slate-400">Current</div>
                  <div className="font-semibold text-sm">
                    {fmtMoney(b.current_balance)} {b.currency || 'SAR'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StepDetails({ form, setForm, selectedType, partners, addPartner }) {
  const needsCustomCounter = form.adjustment_type === 'other';
  const isOwnerType = OWNER_TYPES.includes(form.adjustment_type);
  const [newPartner, setNewPartner] = React.useState('');
  const [addingPartner, setAddingPartner] = React.useState(false);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        أدخِل تفاصيل التَعديل. الـ Policy Engine يَختار الحساب المُقابِل.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 block mb-1">
            <CalendarDays className="w-3 h-3 inline" /> Date *
          </label>
          <input
            type="date"
            data-testid="wizard-date"
            value={form.adjustment_date}
            onChange={(e) => setForm({ ...form, adjustment_date: e.target.value })}
            className="form-input w-full text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Amount (SAR) *</label>
          <input
            type="number" step="0.01" min="0"
            data-testid="wizard-amount"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="form-input w-full text-sm"
            placeholder="0.00"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-500 block mb-1">Description *</label>
        <input
          type="text"
          data-testid="wizard-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="form-input w-full text-sm"
          placeholder="مثل: عمولة شهرية لـ يناير"
        />
      </div>

      <div>
        <label className="text-xs text-slate-500 block mb-1">Reference (optional)</label>
        <input
          type="text"
          data-testid="wizard-reference"
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
          className="form-input w-full text-sm"
          placeholder="STM-001 / صورة الإشعار"
        />
      </div>

      {/* VAT block — only for bank_charge.
          When the bank issues a tax invoice the gross fee must be split
          into net (expense) + VAT (recoverable input). Otherwise ZATCA
          input-VAT is lost and the return is wrong. */}
      {form.adjustment_type === 'bank_charge' && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-indigo-900 font-medium">
            <input
              type="checkbox"
              data-testid="wizard-has-tax-invoice"
              checked={!!form.has_tax_invoice}
              onChange={(e) => setForm({
                ...form,
                has_tax_invoice: e.target.checked,
                ...(e.target.checked ? {} : { vat_amount: '' }),
              })}
              className="w-4 h-4"
            />
            لَدَيّ فاتورة ضَريبية من البنك / Bank issued a tax invoice
          </label>
          {form.has_tax_invoice && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">
                    VAT Amount (SAR)
                  </label>
                  <input
                    type="number" step="0.01" min="0"
                    data-testid="wizard-vat-amount"
                    value={form.vat_amount}
                    onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                    className="form-input w-full text-sm"
                    placeholder="0.49"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">
                    Net (auto)
                  </label>
                  <div
                    className="form-input w-full text-sm bg-slate-50 text-slate-700"
                    data-testid="wizard-net-amount"
                  >
                    {fmtMoney(Math.max(0, (parseFloat(form.amount) || 0) - (parseFloat(form.vat_amount) || 0)))}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-indigo-800">
                سَيُسَجَّل قَيد مُحاسَبي ثُلاثي: <code>Dr EXP_BANK_CHARGES</code> (net) + <code>Dr VAT_INPUT</code> (vat) + <code>Cr Bank</code> (gross).
              </p>
            </>
          )}
        </div>
      )}

      {isOwnerType && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2" dir="rtl">
          <label className="text-sm text-violet-900 font-medium block">
            حساب جاري الشريك / المالك *
          </label>
          <select
            data-testid="wizard-partner-select"
            value={form.counter_account_id}
            onChange={(e) => setForm({ ...form, counter_account_id: e.target.value })}
            className="form-select w-full text-sm"
          >
            <option value="">اختر الشريك…</option>
            {partners.map(p => (
              <option key={p.id} value={p.id}>
                {p.account_name} ({p.account_number})
              </option>
            ))}
          </select>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              data-testid="wizard-new-partner-name"
              value={newPartner}
              onChange={(e) => setNewPartner(e.target.value)}
              className="form-input flex-1 text-sm"
              placeholder="اسم شريك جديد…"
            />
            <button
              type="button"
              data-testid="wizard-add-partner-btn"
              disabled={!newPartner.trim() || addingPartner}
              onClick={async () => {
                setAddingPartner(true);
                await addPartner(newPartner.trim());
                setNewPartner('');
                setAddingPartner(false);
              }}
              className="px-3 py-2 text-xs rounded bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40 whitespace-nowrap"
            >
              + إضافة شريك
            </button>
          </div>
          <p className="text-[11px] text-violet-800">
            {form.adjustment_type === 'owner_withdrawal'
              ? 'القيد: مدين جاري الشريك / دائن البنك — حركة حقوق ملكية بدون ضريبة ولا تدخل في مصاريف الشركة.'
              : 'القيد: مدين البنك / دائن جاري الشريك — ضخ أموال من الشريك للشركة.'}
          </p>
        </div>
      )}

      {needsCustomCounter && (
        <div>
          <label className="text-xs text-slate-500 block mb-1">
            Counter Account Code * (system_code)
          </label>
          <input
            type="text"
            data-testid="wizard-counter-code"
            value={form.counter_account_code}
            onChange={(e) => setForm({ ...form, counter_account_code: e.target.value })}
            className="form-input w-full text-sm font-mono"
            placeholder="e.g. REVENUE_OTHER / EXP_OTHER"
          />
          <p className="text-[11px] text-amber-700 mt-1">
            النوع &quot;أخرى&quot; يَتَطَلَّب system_code صريح للحساب المُقابِل.
          </p>
        </div>
      )}

      {!needsCustomCounter && !isOwnerType && selectedType?.default_counter && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs">
          <div className="font-medium text-blue-900 mb-1">
            Auto-resolved counter account
          </div>
          <code className="text-blue-700">{selectedType.default_counter}</code>
          <span className="text-blue-700">
            {' '}· direction = {selectedType.default_direction.toUpperCase()}
          </span>
        </div>
      )}
    </div>
  );
}

function StepReview({ form, selectedType, selectedBank, partners }) {
  const amt = parseFloat(form.amount) || 0;
  const vat = parseFloat(form.vat_amount) || 0;
  const net = Math.max(0, amt - vat);
  const direction = selectedType?.default_direction || 'debit';
  const partner = (partners || []).find(p => p.id === form.counter_account_id);
  const counter = partner
    ? `${partner.account_number} ${partner.account_name}`
    : (form.counter_account_code || selectedType?.default_counter);
  const showVat = form.adjustment_type === 'bank_charge' && form.has_tax_invoice && vat > 0;
  return (
    <div className="space-y-3" data-testid="wizard-review">
      <p className="text-sm text-slate-600">
        راجِع التَفاصيل قبل الترحيل. سَيُنشَأ مُستند + قيد محاسبي واحد.
      </p>
      <div className="rounded-lg border border-slate-200 p-3 text-sm">
        <Row label="Type"        value={selectedType?.label_en || form.adjustment_type} />
        <Row label="Bank"        value={selectedBank
                                          ? `${selectedBank.bank_name} · ****${(selectedBank.account_number || '').slice(-4)}`
                                          : '-'} />
        <Row label="Date"        value={form.adjustment_date} />
        <Row label="Amount"      value={`${fmtMoney(amt)} SAR`} />
        {showVat && (
          <>
            <Row label="VAT"         value={`${fmtMoney(vat)} SAR`} />
            <Row label="Net"         value={`${fmtMoney(net)} SAR`} />
            <Row label="Tax invoice" value="✔ Confirmed" />
          </>
        )}
        <Row label="Description" value={form.description} />
        {form.reference && <Row label="Reference" value={form.reference} />}
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs">
        <div className="font-bold mb-2">Journal preview</div>
        {direction === 'debit' ? (
          <pre className="font-mono text-amber-900" data-testid="wizard-journal-preview">
{`Dr  Bank                 ${fmtMoney(amt).padStart(12)}
       Cr  ${counter}${' '.repeat(Math.max(0, 18 - (counter || '').length))}${fmtMoney(amt).padStart(12)}`}
          </pre>
        ) : showVat ? (
          <pre className="font-mono text-amber-900" data-testid="wizard-journal-preview">
{`Dr  ${counter}${' '.repeat(Math.max(0, 18 - (counter || '').length))}${fmtMoney(net).padStart(12)}
Dr  VAT_INPUT            ${fmtMoney(vat).padStart(12)}
       Cr  Bank                 ${fmtMoney(amt).padStart(12)}`}
          </pre>
        ) : (
          <pre className="font-mono text-amber-900" data-testid="wizard-journal-preview">
{`Dr  ${counter}${' '.repeat(Math.max(0, 18 - (counter || '').length))}${fmtMoney(amt).padStart(12)}
       Cr  Bank                 ${fmtMoney(amt).padStart(12)}`}
          </pre>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Detail drawer
// ─────────────────────────────────────────────────────────────────────
function DetailDrawer({
  row, isAdmin, voidReason, setVoidReason, voiding,
  onVoid, onClose, onJumpJournal,
}) {
  const types = []; // (passed through; not needed for detail labels)
  const isVoided = row.status === 'voided';
  return (
    <div
      data-testid="adj-detail-drawer"
      className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end"
    >
      <div className="bg-white w-full max-w-md shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="text-base font-bold">
              {row.adjustment_no}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {row.adjustment_date}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <Row label="Type" value={row.adjustment_type} />
          <Row label="Bank" value={row.bank_account_name} />
          <Row label="Direction"
                value={row.direction === 'debit' ? 'Bank ↑ (debit)' : 'Bank ↓ (credit)'} />
          <Row label="Amount" value={`${fmtMoney(row.amount)} SAR`} />
          <Row label="Counter Account"
                value={`${row.counter_account_code} · ${row.counter_account_name || ''}`} />
          <Row label="Description" value={row.description} />
          {row.reference && <Row label="Reference" value={row.reference} />}
          <Row
            label="Status"
            value={
              <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_COLOR[row.status]}`}>
                {STATUS_LABEL[row.status]}
              </span>
            }
          />
          {row.journal_id && (
            <Row
              label="Journal"
              value={
                <button
                  data-testid="detail-jump-journal-btn"
                  onClick={() => onJumpJournal(row.journal_id)}
                  className="text-xs px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 inline-flex items-center gap-1"
                >
                  <BookOpen className="w-3 h-3" /> Open journal
                </button>
              }
            />
          )}
          {isVoided && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-xs space-y-1">
              <div className="font-bold text-rose-800">Void info</div>
              <div>By: {row.voided_by_name || row.voided_by}</div>
              <div>At: {row.voided_at}</div>
              {row.void_reason && <div>Reason: {row.void_reason}</div>}
              {row.reversal_journal_id && (
                <button
                  data-testid="detail-jump-reversal-btn"
                  onClick={() => onJumpJournal(row.reversal_journal_id)}
                  className="mt-1 text-xs px-2 py-1 rounded border border-rose-300 text-rose-800 hover:bg-rose-100 inline-flex items-center gap-1"
                >
                  <BookOpen className="w-3 h-3" /> Open reversal journal
                </button>
              )}
            </div>
          )}
        </div>

        {/* Void action — admin + not already voided */}
        {!isVoided && isAdmin && (
          <div className="border-t border-slate-200 p-4 space-y-2 bg-slate-50">
            <label className="text-xs text-slate-500 block">Void reason (optional)</label>
            <input
              type="text"
              data-testid="detail-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              className="form-input w-full text-sm"
              placeholder="e.g. Posted in error"
            />
            <button
              data-testid="detail-void-btn"
              onClick={onVoid}
              disabled={voiding}
              className="w-full px-4 py-2 text-sm rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {voiding ? <Loader2 className="w-4 h-4 animate-spin" /> :
                          <AlertCircle className="w-4 h-4" />}
              إلغاء وقَيد عكسي / Void & Reverse
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
